/**
 * /drill/guitar — Guitar mini-hub.
 *
 * Lists the three guitar rung tiles (Intervals & Octaves, Triad Shapes,
 * Triad Inversions) in curriculum order with prerequisite locking.
 * Mirrors ExerciseHub's mechanics: mode-tab strip, compact skill cards,
 * REC badge on rung cards.
 *
 * Only accessible when the guitar track is enabled (enabledInstrumentTracks).
 * Challenge mode is deferred for all guitar rungs.
 */

import { useState, useMemo, useEffect, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import {
  buildAllItems,
  buildSkillState,
  recommendedSkillForMode,
  MASTERY_PRESETS,
  type SkillState,
  type ExerciseSkillId,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  loadReviewsSince,
  loadAllListenCounts,
} from "@music-app/persistence";
import type { ReviewSummary } from "@music-app/learning-system";

// ─── Constants ─────────────────────────────────────────────────────────────────

const OXBLOOD = "#872932";
const INK = "#1A1813";
const INK_FADED = "#6A645A";
const PAPER = "#FAF6EE";
const PAPER_WARM = "#F1EBDF";
const SUCCESS = "#0F6E56";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const LS_TAB_KEY = "guitar-hub-tab";

type GuitarMode = "listen" | "practice" | "review";
const ALL_MODES: GuitarMode[] = ["listen", "practice", "review"];

const MODE_COLOR: Record<GuitarMode, string> = {
  listen:   "#4A7FA5",
  practice:    "#5A5550",
  review: "#3D7A5F",
};
const MODE_TITLE: Record<GuitarMode, string> = {
  listen:   "Listen",
  practice:    "Practice",
  review: "Review",
};
const MODE_DESC: Record<GuitarMode, string> = {
  listen:   "Passive listening. No input required. No schedule changes.",
  practice:    "Repeat at your own pace. No schedule changes.",
  review: "Updates your FSRS schedule.",
};

// ─── Rung metadata ─────────────────────────────────────────────────────────────

interface RungMeta {
  id: ExerciseSkillId;
  route: string;
  rungLabel: string;
  title: string;
  subtitle: string;
  /** null means always unlocked */
  prereqSkillId: ExerciseSkillId | null;
  /** nodeId of the MDX lesson to link, if one is authored. */
  lessonNodeId?: string;
}

const RUNGS: RungMeta[] = [
  {
    id: "fret-intervals",
    route: "/guitar/fret-intervals",
    rungLabel: "Rung 1",
    title: "Intervals & Octaves",
    subtitle: "Hear an interval above the root — tap its upper note on the neck.",
    prereqSkillId: null,
    lessonNodeId: "fret-octave-shapes",
  },
  {
    id: "fret-scale-degrees",
    route: "/guitar/fret-scale-degrees",
    rungLabel: "Rung 2",
    title: "Scale Degrees",
    subtitle: "Hear a scale degree in key — tap it on the neck, relative to the lit tonic.",
    prereqSkillId: "fret-intervals",
    lessonNodeId: "fret-scale-degrees-major",
  },
  {
    id: "fret-triads",
    route: "/guitar/fret-triads",
    rungLabel: "Rung 3",
    title: "Triad Shapes",
    subtitle: "Hear a triad quality — build it on the string set.",
    prereqSkillId: "fret-intervals",
    lessonNodeId: "fret-triad-shapes",
  },
  {
    id: "fret-inversions",
    route: "/guitar/fret-inversions",
    rungLabel: "Rung 4",
    title: "Triad Inversions",
    subtitle: "Hear which inversion — place it with the bass note lowest.",
    prereqSkillId: "fret-triads",
    lessonNodeId: "fret-triad-inversions",
  },
  {
    id: "fret-caged",
    route: "/guitar/fret-caged",
    rungLabel: "Rung 5",
    title: "CAGED Shapes",
    subtitle: "Hear the chord — find the root of the named shape on the neck.",
    prereqSkillId: "fret-inversions",
    lessonNodeId: "fret-caged-shapes-ea",
  },
  {
    id: "fret-caged-anchoring",
    route: "/guitar/fret-caged-anchoring",
    rungLabel: "Rung 6",
    title: "CAGED Anchoring",
    subtitle: "Hear the chord — no shape name given, just the dyad. Tap the root.",
    prereqSkillId: "fret-caged",
    lessonNodeId: "fret-caged-anchoring",
  },
  {
    id: "fret-scale-degrees-caged",
    route: "/guitar/fret-scale-degrees-caged",
    rungLabel: "Rung 7",
    title: "Scale Degrees in Shapes",
    subtitle: "The tonic shape is lit — find the scale degrees that fill in the box.",
    prereqSkillId: "fret-caged-anchoring",
    lessonNodeId: "fret-scale-degrees-caged",
  },
  {
    id: "fret-diatonic",
    route: "/guitar/fret-diatonic",
    rungLabel: "Rung 8",
    title: "Diatonic Triads",
    subtitle: "Hear the key — find the diatonic chord's root in the CAGED shape.",
    prereqSkillId: "fret-caged-anchoring",
    lessonNodeId: "fret-diatonic-triads",
  },
  {
    id: "fret-diatonic-minor",
    route: "/guitar/fret-diatonic-minor",
    rungLabel: "Rung 9",
    title: "Minor Key Diatonic Triads",
    subtitle: "Hear the minor key — find the chord's root in the CAGED shape. V is always major.",
    prereqSkillId: "fret-diatonic",
    lessonNodeId: "fret-diatonic-triads-minor",
  },
  {
    id: "fret-seventh",
    route: "/guitar/fret-seventh",
    rungLabel: "Rung 10",
    title: "7th Chord Shapes",
    subtitle: "Three notes given (3rd, 5th, 7th) — tap the root of the maj7, min7, or dom7.",
    prereqSkillId: "fret-diatonic-minor",
    lessonNodeId: "fret-seventh-shapes",
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export function GuitarHubPage(): ReactNode {
  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);
  const guitarEnabled = enabledInstrumentTracks.includes("guitar");

  // Redirect to settings if guitar is not enabled.
  if (!guitarEnabled) {
    return <Navigate to="/settings" replace />;
  }

  return <GuitarHubContent />;
}

function GuitarHubContent(): ReactNode {
  const cards = useCardsStore((s) => s.cards);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const unlockEverything = usePrefsStore((s) => s.unlockEverything);
  const guitarPlacementCompleted = usePrefsStore((s) => s.guitarPlacementCompleted);
  const setGuitarPlacementCompleted = usePrefsStore((s) => s.setGuitarPlacementCompleted);
  const thresholds = MASTERY_PRESETS[masteryPreset];
  const items = useMemo(() => buildAllItems(), []);
  const [activeTab, setActiveTab] = useState<GuitarMode>(() => {
    const saved = localStorage.getItem(LS_TAB_KEY) as GuitarMode | null;
    return saved && ALL_MODES.includes(saved) ? saved : "practice";
  });

  // ── Async data ─────────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  useEffect(() => {
    const since = new Date(Date.now() - SEVEN_DAYS_MS).toISOString();
    loadReviewsSince(since)
      .then((recs) =>
        setReviews(recs.map((r) => ({ itemId: r.itemId, correct: r.correct, timestamp: r.timestamp }))),
      )
      .catch(() => {/* non-fatal */});
  }, []);

  const [listenCounts, setListenCounts] = useState<Partial<Record<ExerciseSkillId, number>>>({});
  useEffect(() => {
    loadAllListenCounts()
      .then((counts) => setListenCounts(counts as Partial<Record<ExerciseSkillId, number>>))
      .catch(() => {/* non-fatal */});
  }, []);

  // ── Skill states ───────────────────────────────────────────────────────────
  const skillStates = useMemo(() => {
    const guitarIds: ExerciseSkillId[] = ["fret-intervals", "fret-scale-degrees", "fret-triads", "fret-inversions", "fret-caged", "fret-caged-anchoring", "fret-scale-degrees-caged", "fret-diatonic", "fret-diatonic-minor", "fret-seventh"];
    return guitarIds.map((id) =>
      buildSkillState(id, items, cards, thresholds, reviews, listenCounts[id] ?? 0),
    );
  }, [items, cards, thresholds, reviews, listenCounts]);

  const skillMap = useMemo(
    () => Object.fromEntries(skillStates.map((s) => [s.id, s])) as Record<ExerciseSkillId, SkillState>,
    [skillStates],
  );

  const recSkill = useMemo(
    () => recommendedSkillForMode(activeTab === "review" ? "practice" : activeTab, skillStates),
    [activeTab, skillStates],
  );

  const handleTabClick = (tab: GuitarMode) => {
    setActiveTab(tab);
    localStorage.setItem(LS_TAB_KEY, tab);
  };

  return (
    <div className="max-w-5xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        eyebrow="guitar track"
        title="Guitar Hub"
        subhead="Harmony on the neck — ten rungs covering intervals, triad shapes, inversions, CAGED shapes, anchoring, scale degrees in shapes, diatonic triads (major & minor), and 7th chord shapes."
      />

      <GuitarPlacementBanner
        completed={guitarPlacementCompleted}
        onSkip={() => void setGuitarPlacementCompleted(true)}
      />

      {/* Mode tab strip */}
      <GuitarModeTabStrip activeTab={activeTab} onTabClick={handleTabClick} />

      {/* Rung tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
        {RUNGS.map((rung) => {
          const skill = skillMap[rung.id];
          if (!skill) return null;

          const isLocked =
            !unlockEverything &&
            rung.prereqSkillId != null &&
            (skillMap[rung.prereqSkillId]?.exercise.mastered ?? 0) === 0;

          return (
            <RungCard
              key={rung.id}
              rung={rung}
              skill={skill}
              activeTab={activeTab}
              isLocked={isLocked}
              isRec={recSkill?.id === rung.id}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── GuitarPlacementBanner ────────────────────────────────────────────────────

function GuitarPlacementBanner({
  completed,
  onSkip,
}: {
  completed: boolean;
  onSkip: () => void;
}): ReactNode {
  if (completed) {
    return (
      <div className="mb-5 flex justify-end">
        <Link
          to="/guitar/placement"
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-accent transition-colors"
        >
          Retake guitar placement →
        </Link>
      </div>
    );
  }
  return (
    <div className="border border-ink/10 bg-paperWarm p-5 mb-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-1.5">
        place yourself on the neck
      </p>
      <p className="font-serif italic text-inkSoft text-sm mb-4 max-w-xl">
        A short fretboard diagnostic — we use the shapes you already know (CAGED,
        diatonic chords, scale degrees) to start you at the right rung instead of
        grinding up from the bottom.
      </p>
      <div className="flex gap-3 flex-wrap items-center">
        <Link to="/guitar/placement" className={buttonClasses("primary")}>
          Take placement →
        </Link>
        <button
          type="button"
          onClick={onSkip}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

// ─── GuitarModeTabStrip ───────────────────────────────────────────────────────

function GuitarModeTabStrip({
  activeTab,
  onTabClick,
}: {
  activeTab: GuitarMode;
  onTabClick: (tab: GuitarMode) => void;
}): ReactNode {
  const activeColor = MODE_COLOR[activeTab];
  return (
    <div className="mb-5">
      <div
        className="grid grid-cols-3 border-b border-ink/20"
        role="tablist"
        aria-label="Guitar drill mode"
      >
        {ALL_MODES.map((tab) => {
          const isActive = activeTab === tab;
          const color = MODE_COLOR[tab];
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabClick(tab)}
              className={`relative px-3 py-3 -mb-px text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30${isActive ? "" : " hover:bg-ink/5"}`}
              style={{
                backgroundColor: isActive ? PAPER_WARM : undefined,
                borderBottom: `3px solid ${isActive ? color : "transparent"}`,
              }}
            >
              <span
                className="font-serif text-[18px] font-medium leading-tight"
                style={{ color: isActive ? color : INK }}
              >
                {MODE_TITLE[tab]}
                {tab === "review" && (
                  <span
                    className="font-mono text-[9px] ml-1 align-middle"
                    style={{ color: SUCCESS }}
                    aria-label="Counts toward Today"
                    title="Counts toward Today"
                  >
                    ●
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div
        className="bg-paperWarm px-4 py-2.5 mt-2"
        style={{ borderLeft: `4px solid ${activeColor}` }}
      >
        <p className="font-serif italic text-sm text-inkSoft">
          {MODE_DESC[activeTab]}
          {" · "}
          <span style={{ color: activeTab === "review" ? SUCCESS : INK_FADED }}>
            {activeTab === "review" ? "Counts" : "Free"}
          </span>
        </p>
      </div>
    </div>
  );
}

// ─── RungCard ─────────────────────────────────────────────────────────────────

function RungCard({
  rung,
  skill,
  activeTab,
  isLocked,
  isRec,
}: {
  rung: RungMeta;
  skill: SkillState;
  activeTab: GuitarMode;
  isLocked: boolean;
  isRec: boolean;
}): ReactNode {
  const [hovered, setHovered] = useState(false);
  const modeColor = MODE_COLOR[activeTab];

  const { mastered, total, lapse7d } = skill.exercise;
  const lapsing = lapse7d >= 0.25;
  const barFill = total === 0 ? 0 : mastered / total;

  const fgPrimary = hovered ? PAPER : INK;
  const fgFaded = hovered ? `${PAPER}88` : INK_FADED;
  const barTrack = hovered ? "rgba(255,255,255,0.2)" : "rgba(26,24,19,0.08)";
  const barFillCol = hovered ? "rgba(255,255,255,0.85)" : lapsing ? OXBLOOD : SUCCESS;

  const cardContent = (
    <>
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.18em] truncate leading-none transition-colors duration-150"
          style={{ color: fgFaded }}
        >
          {rung.rungLabel}
        </span>
        <div className="flex-shrink-0">
          {isRec && !isLocked ? (
            <span
              className="font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 leading-none transition-colors duration-150"
              style={hovered
                ? { backgroundColor: PAPER, color: modeColor }
                : { backgroundColor: modeColor, color: PAPER }}
            >
              ★ REC
            </span>
          ) : isLocked ? (
            <span className="font-mono text-[9px] uppercase tracking-wider text-inkFaded px-1 py-0.5 leading-none">
              🔒
            </span>
          ) : null}
        </div>
      </div>

      <h2
        className="font-serif text-[20px] font-medium leading-snug mb-1 transition-colors duration-150"
        style={{ color: fgPrimary }}
      >
        {rung.title}
      </h2>
      <p
        className="font-serif italic text-[12px] leading-snug mb-3 transition-colors duration-150"
        style={{ color: fgFaded }}
      >
        {rung.subtitle}
      </p>

      <div
        className="w-full h-[4px] mb-3 transition-colors duration-150"
        style={{ backgroundColor: barTrack }}
        role="progressbar"
        aria-valuenow={Math.round(barFill * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full transition-colors duration-150"
          style={{
            width: `${Math.min(barFill, 1) * 100}%`,
            backgroundColor: isLocked ? INK_FADED : barFillCol,
            opacity: isLocked ? 0.3 : 1,
          }}
        />
      </div>

      <p
        className="font-serif text-[17px] font-medium leading-none tabular-nums mb-1 transition-colors duration-150"
        style={{ color: isLocked ? INK_FADED : fgPrimary }}
      >
        {isLocked ? "🔒" : `${mastered}/${total}`}
      </p>
      <p
        className="font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-150"
        style={{ color: isLocked ? INK_FADED : hovered ? `${PAPER}BB` : lapsing ? OXBLOOD : INK_FADED }}
      >
        {isLocked ? "complete rung 1 first" : lapsing ? `${Math.round(lapse7d * 100)}% lapse` : "mastered"}
      </p>
    </>
  );

  const lessonLink = rung.lessonNodeId ? (
    <div className="border-t border-ink/5 px-3 py-2">
      <Link
        to={`/learn/${rung.lessonNodeId}?from=/guitar`}
        className="font-mono text-[9px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors"
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
      >
        Read →
      </Link>
    </div>
  ) : null;

  if (isLocked) {
    return (
      <div className="flex flex-col border border-ink/20 bg-paper">
        <div className="p-3" style={{ opacity: 0.55, cursor: "not-allowed" }}>
          {cardContent}
        </div>
        {lessonLink}
      </div>
    );
  }

  const containerStyle: React.CSSProperties = hovered
    ? { border: `1px solid ${modeColor}`, backgroundColor: modeColor }
    : { border: "1px solid rgba(26,24,19,0.2)", backgroundColor: PAPER };

  return (
    <div className="flex flex-col transition-colors duration-150" style={containerStyle}>
      <Link
        to={`${rung.route}?mode=${activeTab}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30"
        aria-label={`${rung.title} — ${activeTab} mode`}
      >
        <div className="flex flex-col p-3 h-full">
          {cardContent}
        </div>
      </Link>
      {lessonLink}
    </div>
  );
}
